"use client";

import * as React from "react";
import Link from "next/link";
import {
  Compass,
  Lock,
  WifiOff,
  ArrowLeftRight,
  TriangleAlert,
} from "lucide-react";
import {
  Button,
} from "@/components/ui/button";
import {
  EmptyState,
} from "@/components/state/empty-states";

/** Wraps any error state so it drops into a page body or a card slot unchanged. */
function ErrorFrame({
  children,
  footnote,
}: {
  children: React.ReactNode;
  footnote?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      {children}
      {footnote ? (
        <p className="pb-2 text-xs text-muted-foreground">{footnote}</p>
      ) : null}
    </div>
  );
}

export function FetchError({
  resource = "certificates",
  onRetry,
  reference,
}: {
  resource?: string;
  onRetry?: () => void;
  /** Support reference from the API, if the response carried one. */
  reference?: string;
}) {
  return (
    <ErrorFrame footnote={reference ? `Reference ${reference}` : undefined}>
      <EmptyState
        icon={WifiOff}
        title={`Couldn’t load ${resource}`}
        description={`The request timed out. Nothing was changed — your ${resource} are safe.`}
        action={
          <div className="flex gap-2">
            <Button onClick={onRetry}>Try again</Button>
            <Button variant="outline" asChild>
              <a href="mailto:support@sigalmark.com">Contact support</a>
            </Button>
          </div>
        }
      />
    </ErrorFrame>
  );
}

/**
 * Permission denied always names the ACTIVE tenant and the user's role in it,
 * and always offers two exits: switch tenant, or go back to certificates.
 */
export function PermissionDenied({
  what = "This page",
  requiredRole = "an admin role",
  activeTenantName,
  activeRoleLabel,
  otherTenantHint,
  onSwitchTenant,
}: {
  what?: string;
  requiredRole?: string;
  activeTenantName: string;
  activeRoleLabel: string;
  /** e.g. "you’re an admin of Alpha Institute" — omit when they administer nothing. */
  otherTenantHint?: string;
  onSwitchTenant?: () => void;
}) {
  return (
    <ErrorFrame footnote={`Ask an admin of ${activeTenantName} to change your role`}>
      <EmptyState
        icon={Lock}
        title="You don’t have access to this page"
        description={`${what} needs ${requiredRole}. You’re signed in as a ${activeRoleLabel} of ${activeTenantName}${
          otherTenantHint ? ` — ${otherTenantHint}` : ""
        }.`}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={onSwitchTenant} className="gap-2">
              <ArrowLeftRight className="size-4" />
              Switch tenant
            </Button>
            <Button variant="outline" asChild>
              <Link href="/certificates">Back to certificates</Link>
            </Button>
          </div>
        }
      />
    </ErrorFrame>
  );
}

export function NotFound({
  subject = "certificate",
}: {
  subject?: string;
}) {
  return (
    <EmptyState
      icon={Compass}
      title={`We can’t find that ${subject}`}
      description="It may have been revoked, or the link may be mistyped. Verification links are case-sensitive."
      action={
        <div className="flex gap-2">
          <Button asChild>
            <Link href="/certificates">Back to certificates</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href="/verify">Verify a code</Link>
          </Button>
        </div>
      }
    />
  );
}

/**
 * Some screens deliberately keep rendering when a secondary request fails, so
 * the page is still usable. Without this the fallback is indistinguishable from
 * real data — an empty invitations list, a subscription that looks healthy.
 * Say so, inline, without blocking the rest of the page.
 */
export function PartialDataNotice({ what, reason }: { what: string; reason?: string | null }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-[var(--radius)] border border-warning-border bg-warning px-4 py-3 text-warning-foreground"
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0" />
      <p className="text-[13px] leading-relaxed">
        {what} could not be loaded, so what you see here may be incomplete.
        {reason ? <span className="opacity-80"> {reason}</span> : null}
      </p>
    </div>
  );
}
